/**
 * A promotion's structured offer in words (backend B3.5, master D-10): the
 * discount phrase, "where it's valid" and the link to the brand's own offer
 * page.
 *
 * The discount comes ONLY from `discountType` / `discountMin` / `discountMax`
 * (web plan, "Title / H1 formulas": never parsed from the name, the headline
 * or the caption) and is left out when they say nothing. Pure (no React, no
 * `window`): the head formulas (./templates, shared with the SEO server) and
 * the promotion page read the same answers. The words come from the i18n
 * catalogue through the caller's `t` (web plan W3.0).
 */
import type { Translate } from '@/i18n';

/** A promotion's discount fields as the API sends them. */
export interface DiscountFields {
  discountType?: string | null;
  discountMin?: number | null;
  discountMax?: number | null;
}

/**
 * What the fields say:
 * - `flat` — "Flat 30% off" (FLAT with one number, or min = max);
 * - `range` — "20–50% off" (FLAT with min < max);
 * - `upTo` — "Up to 50% off" (UP_TO's max; also a bare `discountMax` without a
 *   type, the formula's input before B3.5 typed it);
 * - `cashback` / `cashbackUpTo` — "10% cashback" / "Up to 15% cashback";
 * - `bogo` — "Buy 1 get 1 free".
 */
export type DiscountKind = 'flat' | 'range' | 'upTo' | 'cashback' | 'cashbackUpTo' | 'bogo';

export interface StructuredDiscount {
  kind: DiscountKind;
  /** The percentages the phrase shows, as text ("50", "12.5"). */
  min: string | null;
  max: string | null;
  /**
   * The best percentage OFF the price it promises, for a hub's "Up to {X}%
   * Off" across its offers (`liveDeal` in ./templates): FLAT, UP_TO (and an
   * untyped max) only — a cashback is not money off, a BOGO has no percentage.
   */
  percentOff: number | null;
}

/**
 * A percentage the API may send (0–100, a Float) as it can be shown: at most
 * one decimal, rounded DOWN so a phrase never promises more than the data
 * (49.99 → 49.9); null for anything else, and for 0 (no discount to state).
 */
export function discountPercent(value: number | null | undefined): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0 || value > 100) return null;
  const shown = Math.floor(value * 10 + 1e-9) / 10;
  return shown > 0 ? shown : null;
}

/** The structured discount, or null when the fields state none (or a type this module does not know). */
export function structuredDiscount(offer: DiscountFields | null | undefined): StructuredDiscount | null {
  if (!offer) return null;
  const type = offer.discountType ?? null;
  const min = discountPercent(offer.discountMin);
  const max = discountPercent(offer.discountMax);
  const text = (n: number | null) => (n === null ? null : String(n));
  const range = min !== null && max !== null && min < max;

  switch (type) {
    case 'BOGO':
      return { kind: 'bogo', min: null, max: null, percentOff: null };
    case 'FLAT': {
      if (range) return { kind: 'range', min: text(min), max: text(max), percentOff: max };
      const value = max ?? min;
      return value === null ? null : { kind: 'flat', min: null, max: text(value), percentOff: value };
    }
    case 'UP_TO':
      return max === null ? null : { kind: 'upTo', min: null, max: text(max), percentOff: max };
    case 'CASHBACK': {
      if (range) return { kind: 'cashbackUpTo', min: null, max: text(max), percentOff: null };
      const value = max ?? min;
      return value === null ? null : { kind: 'cashback', min: null, max: text(value), percentOff: null };
    }
    case null:
      // No type: only a bare discountMax means something ("up to").
      return max === null ? null : { kind: 'upTo', min: null, max: text(max), percentOff: max };
    default:
      return null;
  }
}

/**
 * The discount as a sentence part: "Up to 50% off", "Flat 30% off",
 * "20–50% off", "10% cashback", "Up to 15% cashback", "Buy 1 get 1 free";
 * `''` when the fields state none.
 */
export function discountPhrase(offer: DiscountFields | null | undefined, t: Translate): string {
  const discount = structuredDiscount(offer);
  if (!discount) return '';
  return t(`promotion:discount.${discount.kind}`, { min: discount.min ?? '', max: discount.max ?? '' });
}

/** `(?<![\d.])50\s*%`: "50%" or "50 %", not the end of "150%" or "2.50%". */
function percentPattern(value: string): RegExp {
  return new RegExp(`(?<![\\d.])${value.replace('.', '\\.')}\\s*%`);
}

const BOGO_PATTERN = /\b(?:bogo|b1g1|buy\s*(?:1|one)\s*get\s*(?:1|one))\b/i;

/**
 * Whether `text` (a headline) already states the discount, so a description
 * does not say it twice: it starts with the phrase, mentions the phrase's top
 * percentage ("Flat 50% Off Everything" for a FLAT 50) or, for a BOGO, says
 * buy-one-get-one. Only ever used to LEAVE OUT the structured phrase; nothing
 * is read from the text.
 */
export function statesDiscount(text: string, offer: DiscountFields | null | undefined, t: Translate): boolean {
  const discount = structuredDiscount(offer);
  if (!discount || !text) return false;
  const phrase = discountPhrase(offer, t);
  if (phrase && text.toLowerCase().startsWith(phrase.toLowerCase())) return true;
  if (discount.kind === 'bogo') return BOGO_PATTERN.test(text);
  return discount.max !== null && percentPattern(discount.max).test(text);
}

// ─── Where it's valid ──────────────────────────────────────────────────────

/** The backend's `AvailabilityScope` values. */
export const AVAILABILITY_SCOPES = Object.freeze([
  'ALL_STORES',
  'SELECTED_STORES',
  'ONLINE_ONLY',
  'ONLINE_AND_STORES',
] as const);

export type KnownAvailabilityScope = (typeof AVAILABILITY_SCOPES)[number];

/** A scope this module knows, or null (none set, or a value added later). */
export function availabilityScope(value: string | null | undefined): KnownAvailabilityScope | null {
  return (AVAILABILITY_SCOPES as readonly string[]).includes(value ?? '') ? (value as KnownAvailabilityScope) : null;
}

/**
 * "Valid at all Bata stores", "Valid at selected Bata stores", "Online only",
 * "Online and at Bata stores" (without a merchant: "…all stores" etc.); `''`
 * when no scope is set.
 */
export function availabilityText(
  scope: string | null | undefined,
  merchant: string | null | undefined,
  t: Translate,
): string {
  const known = availabilityScope(scope);
  if (!known) return '';
  const name = merchant?.trim() ?? '';
  return t(`promotion:availability.${name ? 'named' : 'unnamed'}.${known}`, { name });
}

/**
 * Whether the offer can be used in the merchant's stores at all: false only
 * for ONLINE_ONLY. The page then lists no stores or cities as "where it's
 * valid" (the backend gives such a promotion no cities either).
 */
export function validInStores(scope: string | null | undefined): boolean {
  return availabilityScope(scope) !== 'ONLINE_ONLY';
}

// ─── The brand's own offer page ────────────────────────────────────────────

/**
 * The offer's page on the brand's site, when it is a usable https URL with a
 * public host (the backend's B3.5 rules; checked again here because the page
 * puts it in an `href`); null otherwise. `host` is its name without "www.",
 * for the link text.
 */
export function offerLink(value: string | null | undefined): { href: string; host: string } | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  let url: URL;
  try {
    url = new URL(value.trim());
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.username || url.password) return null;
  const host = url.hostname.toLowerCase();
  if (!host.includes('.') || host.endsWith('.localhost') || /^[\d.]+$/.test(host) || host.startsWith('[')) return null;
  return { href: url.href, host: host.replace(/^www\./, '') };
}
